// src/services/LeaderboardService.ts - Updated for Edge Function integration
import { supabase } from '../lib/supabase';

export interface LeaderboardEntry {
  id: string;
  user_id: string;
  username: string;
  score: number;
  level: number;
  class_type: string;
  created_at: string;
}

export const submitScore = async (scoreData: {
  userId: string;
  username: string;
  score: number;
  level: number;
  classType: string;
}): Promise<{success: boolean; data?: LeaderboardEntry; error?: string}> => {
  try {
    // Check if user is authenticated
    if (!scoreData.userId) {
      return { 
        success: false, 
        error: "You must be logged in to submit scores to the leaderboard" 
      };
    }

    // Get the current session to get the access token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      return {
        success: false,
        error: "Authentication required to submit scores"
      };
    }

    // Call the Edge Function
    const { data, error } = await supabase.functions.invoke('submit-layer-defender-score', {
      body: {
        score: scoreData.score,
        level: scoreData.level,
        class_type: scoreData.classType
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      return { 
        success: false, 
        error: error.message || "Failed to submit score" 
      };
    }

    if (!data.success) {
      return {
        success: false,
        error: data.error || "Failed to submit score"
      };
    }

    return { 
      success: true, 
      data: data.data 
    };
    
  } catch (error) {
    console.error('Error submitting score:', error);
    return { 
      success: false, 
      error: error.message || "Unknown error occurred" 
    };
  }
};

// Keep the existing read functions unchanged since they're already secure
export const getTopScores = async (filter?: string, limit = 20): Promise<{success: boolean; data?: LeaderboardEntry[]; error?: string}> => {
  try {
    let query = supabase
      .from('LayerDefendLeaderboard')
      .select('*')
      .order('score', { ascending: false });
    
    // Apply filter if provided
    if (filter && filter !== 'all') {
      query = query.eq('class_type', filter.toUpperCase());
    }
    
    const { data, error } = await query.limit(limit);
      
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return { success: false, error: error.message || "Unknown error occurred" };
  }
};

export const getUserScores = async (userId: string): Promise<{success: boolean; data?: LeaderboardEntry[]; error?: string}> => {
  try {
    const { data, error } = await supabase
      .from('LayerDefendLeaderboard')
      .select('*')
      .eq('user_id', userId)
      .order('score', { ascending: false });
      
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching user scores:', error);
    return { success: false, error: error.message || "Unknown error occurred" };
  }
};